import type { Product } from 'Services/ProductManagement/Features/Product/productSlice'
import type { ListResponse } from 'Services/ProductManagement/types'
import type { LoadingStates } from 'Store'
import { createAsyncThunkWithErrorHandling, getAxiosInstance } from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createSlice } from '@reduxjs/toolkit'

export enum TariffProductTabType {
  DETAILS = 'details',
  PRICING = 'pricing',
  SALES_CHANNELS = 'sales_channels',
  AVAILABILITY = 'availability'
}

export enum TariffProductsEditTabType {
  PRODUCTS = 'products'
}

export enum TariffCatalogTabType {
  PRODUCTS = 'products',
  DETAILS = 'details',
  ALLOWED_API_CLIENTS = 'allowed_api_clients'
}

export enum TariffStatus {
  ACTIVE = 'ACTIVE',
  PASSIVE = 'PASSIVE',
  DRAFT = 'DRAFT',
  STAGED = 'STAGED',
  ACTIVE_PRE_ORDER = 'ACTIVE_PRE_ORDER'
}

export type TariffStatusType =
  | TariffStatus.ACTIVE
  | TariffStatus.PASSIVE
  | TariffStatus.DRAFT
  | TariffStatus.STAGED
  | TariffStatus.ACTIVE_PRE_ORDER

export interface TariffState {
  total_results: number
  next_page: string | null
  previous_page: string | null
  tariffs: Tariff[]
  allTariffs: Tariff[]
  singleTariff: Tariff | null
  loading: LoadingStates
  singleLoading: LoadingStates
  submitLoading: LoadingStates
  allTariffsLoading: LoadingStates
}

const initialState: TariffState = {
  total_results: 0,
  next_page: null,
  previous_page: null,
  tariffs: [],
  allTariffs: [],
  singleTariff: null,
  loading: 'idle',
  singleLoading: 'idle',
  submitLoading: 'idle',
  allTariffsLoading: 'idle'
}

export interface TariffActivePeriod {
  days_of_week: string[]
  exceptions: string[]
  time_of_day_from: string | null
  time_of_day_until: string | null
  title: string
  valid_from: Date
  valid_now: boolean
  valid_until: Date
  zone_id: string | null
}

export interface Tariff {
  id: string
  created_timestamp: Date
  modified_timestamp: Date
  tariff_code: string
  description: string
  status: TariffStatusType
  active_period: TariffActivePeriod
  pta: string
  product_ids: string[]
  vat_definition_id: string
  pre_order_start_timestamp: Date | null
}

export interface FetchTariffListParams {
  filter?: string
  search_text?: string
  sort_by?: string
  sort_order?: string
  start_index?: string
  count?: number
  bypass_pagination?: boolean
}

const fetchTariffs = createAsyncThunkWithErrorHandling<
  ListResponse<Tariff>,
  FetchTariffListParams | undefined
>('tariff/fetchTariffs', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Tariff>>('/api/v1/tariff/', {
    params
  })
  return response.data
})

const fetchAllTariffs = createAsyncThunkWithErrorHandling<
  ListResponse<Tariff>,
  FetchTariffListParams | undefined
>('tariff/fetchAllTariffs', async data => {
  const { ...params } = data
  const response = await getAxiosInstance().get<ListResponse<Tariff>>(`/api/v1/tariff/`, {
    params
  })
  return response.data
})

const fetchSingleTariff = createAsyncThunkWithErrorHandling<Tariff, { id: string }>(
  'tariff/fetchSingleTariff',
  async data => {
    const response = await getAxiosInstance().get<Tariff>(`/api/v1/tariff/${data.id}/`)
    return response.data
  }
)

const createSingleTariff = createAsyncThunkWithErrorHandling<Tariff, Partial<Tariff>>(
  'tariff/createSingleTariff',
  async data => {
    const response = await getAxiosInstance().post<Tariff>(`/api/v1/tariff/`, data)
    return response.data
  }
)

export interface UpdateTariffRequestData extends Partial<Tariff> {
  added_products?: Product[]
}

const updateSingleTariff = createAsyncThunkWithErrorHandling<
  Tariff,
  { id: string } & UpdateTariffRequestData
>('tariff/updateSingleTariff', async data => {
  const { id, ...fields } = data
  const response = await getAxiosInstance().put<Tariff>(`/api/v1/tariff/${id}/`, fields)
  return response.data
})

const copySingleTariff = createAsyncThunkWithErrorHandling<Tariff, { id: string }>(
  'tariff/copySingleTariff',
  async data => {
    const response = await getAxiosInstance().post<Tariff>(`/api/v1/tariff/${data.id}/copy/`)
    return response.data
  }
)

type ImportSingleTariffResponse = Record<string, never>

const importSingleTariff = createAsyncThunkWithErrorHandling<
  ImportSingleTariffResponse,
  { id: string }
>('tariff/importSingleTariff', async data => {
  const { id } = data
  await getAxiosInstance().post(`/api/v1/tariff/${id}/import/`)
  return {}
})

const genericActions = actionGenerator('tariff')

const tariffSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      /* Tariff list */
      .addCase(fetchTariffs.fulfilled, (state, { payload }) => {
        state.tariffs = payload.resources
        state.total_results = payload.total_results
        state.next_page = payload.next_page
        state.previous_page = payload.previous_page
        state.loading = 'fulfilled'
      })
      .addCase(fetchTariffs.pending, state => {
        state.tariffs = initialState.tariffs
        state.total_results = initialState.total_results
        state.next_page = initialState.next_page
        state.previous_page = initialState.previous_page
        state.loading = 'pending'
      })
      .addCase(fetchTariffs.rejected, state => {
        state.loading = 'rejected'
      })

      /* All tariffs */
      .addCase(fetchAllTariffs.fulfilled, (state, { payload }) => {
        state.allTariffs = payload.resources
        state.total_results = payload.total_results
        state.allTariffsLoading = 'fulfilled'
      })
      .addCase(fetchAllTariffs.pending, state => {
        state.allTariffs = initialState.tariffs
        state.total_results = initialState.total_results
        state.allTariffsLoading = 'pending'
      })
      .addCase(fetchAllTariffs.rejected, state => {
        state.allTariffsLoading = 'rejected'
      })

      /* Single tariff fetch */
      .addCase(fetchSingleTariff.fulfilled, (state, { payload }) => {
        state.singleTariff = payload
        state.singleLoading = 'fulfilled'
      })
      .addCase(fetchSingleTariff.pending, state => {
        state.singleTariff = initialState.singleTariff
        state.singleLoading = 'pending'
      })
      .addCase(fetchSingleTariff.rejected, state => {
        state.singleLoading = 'rejected'
      })

      /* Single tariff create */
      .addCase(createSingleTariff.fulfilled, (state, { payload }) => {
        state.singleTariff = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createSingleTariff.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createSingleTariff.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Single tariff update */
      .addCase(updateSingleTariff.fulfilled, (state, { payload }) => {
        state.singleTariff = payload
        state.tariffs = state.tariffs.map(tariff => (tariff.id === payload.id ? payload : tariff))
        state.singleLoading = 'fulfilled'
      })
      .addCase(updateSingleTariff.pending, state => {
        state.singleLoading = 'pending'
      })
      .addCase(updateSingleTariff.rejected, state => {
        state.singleLoading = 'rejected'
      })

      /* Single tariff copy */
      .addCase(copySingleTariff.fulfilled, (state, { payload }) => {
        state.singleTariff = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(copySingleTariff.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(copySingleTariff.rejected, state => {
        state.submitLoading = 'rejected'
      })

      /* Single tariff import */
      .addCase(importSingleTariff.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(importSingleTariff.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(importSingleTariff.rejected, state => {
        state.submitLoading = 'rejected'
      })
  }
})

export default tariffSlice.reducer

export {
  copySingleTariff,
  createSingleTariff,
  fetchAllTariffs,
  fetchSingleTariff,
  fetchTariffs,
  updateSingleTariff,
  importSingleTariff
}
